import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, from, of } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { PreloadedFile } from '../../shared/components/custom-upload-area/custom-upload-area.component';
import { CheckStatusResponse, GetPatientResponse } from '../../models/patient.model';
import { RegistrationService } from './registration.service';
import { SelectOption } from '../../shared/components/custom-select';
import { SnackbarService } from '../../services/snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';

export interface RegistrationFormData {
  firstName: string;
  middleInitial: string;
  lastName: string;
  suffix: string;
  countryCode: string;
  mobileNumber: string;
  email: string;
  region: any;
  city: any;
  prescribedProduct: any;
  validId: File[];
  prescription: File[];
  proofOfTravel: File[];
  schema_code?: string;
  url_code?: string;
  registration_mode?: string;
  // patient_id removed - will be derived from authentication context
  // Preloaded file metadata
  validIdMetadata?: Array<{ filename: string; presignedUrl: string; s3_key?: string }>;
  prescriptionMetadata?: Array<{ filename: string; presignedUrl: string; s3_key?: string }>;
  proofOfTravelMetadata?: Array<{ filename: string; presignedUrl: string; s3_key?: string }>;
}

/**
 * @deprecated Use RegistrationFormData with integrated file metadata instead
 */
export interface PreloadedFiles {
  govId: PreloadedFile[];
  prescription: PreloadedFile[];
  proofOfTravel: PreloadedFile[];
}

@Injectable({
  providedIn: 'root'
})
export class RegistrationStateService {
  private registrationService = inject(RegistrationService);
  private snackbarService = inject(SnackbarService);
  private translate = inject(TranslateService);
  
  readonly countryCodeOptions = [
    { value: '+63', label: '+63' },
    { value: '+1', label: '+1' },
    { value: '+44', label: '+44' },
    { value: '+86', label: '+86' }
  ];

  readonly suffixOptions = [
    { value: null, label: 'N/A'},
    { value: 'jr', label: 'Jr.' },
    { value: 'sr', label: 'Sr.' },
    { value: 'ii', label: 'II' },
    { value: 'iii', label: 'III' }
  ];

  readonly productOptions = [
    { value: '1', label: 'Apixaban (Eliquis)' }
  ];
  
  private registrationData: Partial<RegistrationFormData> | null = {};
  private isRefill = false;
  private preloadedFiles: PreloadedFiles = {
    govId: [],
    prescription: [],
    proofOfTravel: []
  };
  private rejectionReason = '';
  private uploadedValidIdFiles: File[] = [];
  private uploadedPrescriptionFiles: File[] = [];
  private uploadedProofOfTravelFiles: File[] = [];
  private prescriptionFormState: { travelingAbroad: boolean; patientConsent: boolean; termsConditions: boolean } | null = null;
  
  private regionsSubject = new BehaviorSubject<SelectOption[]>([]);
  private citiesSubject = new BehaviorSubject<SelectOption[]>([]);
  private loadingRegionsSubject = new BehaviorSubject<boolean>(false);
  private loadingCitiesSubject = new BehaviorSubject<boolean>(false);
  private citiesLoadedSubject = new BehaviorSubject<boolean>(false);
  
  regions$ = this.regionsSubject.asObservable();
  cities$ = this.citiesSubject.asObservable();
  loadingRegions$ = this.loadingRegionsSubject.asObservable();
  loadingCities$ = this.loadingCitiesSubject.asObservable();
  citiesLoaded$ = this.citiesLoadedSubject.asObservable();
  modalEligibilityConfirmed:boolean = false;
  
  private patientConsentAccepted = false;
  private termsConditionsAccepted = false;

  setRegistrationData(data: Partial<RegistrationFormData>): void {
    console.log('Setting registration data:', data);
    console.log('Setting registration data:', this.registrationData);
    this.registrationData = data;
    if (data.validId && data.validId.length > 0) {
      this.uploadedValidIdFiles = data.validId;
    }
    if (data.prescription && data.prescription.length > 0) {
      this.uploadedPrescriptionFiles = data.prescription;
    }
    if (data.proofOfTravel && data.proofOfTravel.length > 0) {
      this.uploadedProofOfTravelFiles = data.proofOfTravel;
    }
  }

  getRegistrationData(): Partial<RegistrationFormData> | null {
    if (this.registrationData && this.uploadedValidIdFiles.length > 0) {
      return { ...this.registrationData, validId: this.uploadedValidIdFiles };
    }
    return this.registrationData;
  }

  setIsRefill(isRefill: boolean): void {
    this.isRefill = isRefill;
  }

  getIsRefill(): boolean {
    return this.isRefill;
  }

  /**
   * @deprecated Use getRegistrationData() to access file data instead
   */
  setPreloadedFiles(files: PreloadedFiles): void {
    this.preloadedFiles = files;
  }

  /**
   * @deprecated Use getRegistrationData() to access file data instead
   */
  getPreloadedFiles(): PreloadedFiles {
    return this.preloadedFiles;
  }

  setRejectionReason(reason: string): void {
    this.rejectionReason = reason;
  }

  getRejectionReason(): string {
    return this.rejectionReason;
  }

  getUploadedValidIdFiles(): File[] {
    return this.uploadedValidIdFiles;
  }

  getUploadedPrescriptionFiles(): File[] {
    return this.uploadedPrescriptionFiles;
  }

  getUploadedProofOfTravelFiles(): File[] {
    return this.uploadedProofOfTravelFiles;
  }

  getPatientData(): Partial<RegistrationFormData> | null {
    return this.registrationData;
  }

  hasRegistrationData(): boolean {
    return !!this.registrationData;
  }

  loadRegions(): void {
    this.loadingRegionsSubject.next(true);
    this.registrationService.getRegions({ offset: 0, limit: 50, keyword: '' })
      .subscribe({
        next: (response) => {
          this.loadingRegionsSubject.next(false);
          if (response?.value === 'Success') {
            this.regionsSubject.next(response.data.map(r => ({ value: r.id, label: r.description })));
          }
        },
        error: () => {
          this.loadingRegionsSubject.next(false);
          this.snackbarService.show(this.translate.instant('ERRORS.REGIONS_LOAD_FAILED'));
        }
      });
  }

  loadCities(regionId: number, keyword = ''): void {
    this.loadingCitiesSubject.next(true);
    this.citiesLoadedSubject.next(false);
    this.registrationService.getCities({ offset: 0, limit: 50, keyword, province_id: 0, region_id: regionId })
      .subscribe({
        next: (response) => {
          this.loadingCitiesSubject.next(false);
          if (response?.value === 'Success') {
            this.citiesSubject.next(response.data.map(c => ({ value: c.id, label: c.description })));
            this.citiesLoadedSubject.next(true);
          }
        },
        error: () => {
          this.loadingCitiesSubject.next(false);
          this.citiesLoadedSubject.next(false);
          this.snackbarService.show(this.translate.instant('ERRORS.CITIES_LOAD_FAILED'));
        }
      });
  }

  searchCities(regionId: number, keyword: string): Observable<any> {
    return this.registrationService.getCities({ offset: 0, limit: 50, keyword, region_id: regionId });
  }

  clearState(): void {
    this.registrationData = {};
    this.isRefill = false;
    this.rejectionReason = '';
    this.uploadedValidIdFiles = [];
    this.uploadedPrescriptionFiles = [];
    this.uploadedProofOfTravelFiles = [];
    this.prescriptionFormState = null;
    this.regionsSubject.next([]);
    this.citiesSubject.next([]);
    this.citiesLoadedSubject.next(false);
    this.loadingRegionsSubject.next(false);
    this.loadingCitiesSubject.next(false);
    this.patientConsentAccepted = false;
    this.termsConditionsAccepted = false;
    this.modalEligibilityConfirmed = false;
    // Deprecated - kept for backward compatibility
    this.preloadedFiles = { govId: [], prescription: [], proofOfTravel: [] };
  }

  mapCheckStatusToRegistrationData(responseData: CheckStatusResponse): Observable<void> {
    const files = responseData.data.files || [];
    const validIdMetadata: Array<{ filename: string; presignedUrl: string; s3_key?: string }> = [];
    const prescriptionMetadata: Array<{ filename: string; presignedUrl: string; s3_key?: string }> = [];
    const proofOfTravelMetadata: Array<{ filename: string; presignedUrl: string; s3_key?: string }> = [];

    if (files.length === 0) {
      this.registrationData = {
        ...this.registrationData,
        validIdMetadata,
        prescriptionMetadata,
        proofOfTravelMetadata
      };
      this.setRejectionReason(responseData.data.approval_details?.rejected_reason || '');
      return of(undefined);
    }

    const fileObservables = files.map((file: any) => {
      const metadata = {
        filename: file.filename,
        presignedUrl: file.presigned_url,
        s3_key: file.s3_key
      };

      if (file.type === 'GOVERNMENT_ID') {
        validIdMetadata.push(metadata);
      } else if (file.type === 'PRESCRIPTION') {
        prescriptionMetadata.push(metadata);
      } else if (file.type === 'PROOF_OF_TRAVEL') {
        proofOfTravelMetadata.push(metadata);
      }

      return from(fetch(file.presigned_url)).pipe(
        switchMap(response => from(response.blob())),
        map(blob => {
          const fileObj = new File([blob], file.filename, { type: blob.type || 'image/png' });
          return { type: file.type, file: fileObj };
        }),
        catchError(() => {
          console.warn(`Failed to load file: ${file.filename}`);
          return of(null);
        })
      );
    });

    return forkJoin(fileObservables).pipe(
      tap(loadedFiles => {
        const validIdFiles: File[] = [];
        const prescriptionFiles: File[] = [];
        const proofOfTravelFiles: File[] = [];

        loadedFiles.forEach(result => {
          if (result) {
            if (result.type === 'GOVERNMENT_ID') {
              validIdFiles.push(result.file);
            } else if (result.type === 'PRESCRIPTION') {
              prescriptionFiles.push(result.file);
            } else if (result.type === 'PROOF_OF_TRAVEL') {
              proofOfTravelFiles.push(result.file);
            }
          }
        });

        this.registrationData = {
          ...this.registrationData,
          validIdMetadata,
          prescriptionMetadata,
          proofOfTravelMetadata
        };

        this.uploadedValidIdFiles = [...(this.registrationData?.validId || []), ...validIdFiles];
        this.uploadedPrescriptionFiles = [...(this.registrationData?.prescription || []), ...prescriptionFiles];
        this.uploadedProofOfTravelFiles = [...(this.registrationData?.proofOfTravel || []), ...proofOfTravelFiles];

        this.setRejectionReason(responseData.data.approval_details?.rejected_reason || '');
      }),
      map(() => undefined)
    );
  }

  /**
   * @deprecated Use mapCheckStatusToRegistrationData instead
   */
  mapCheckStatusToPreloadedFiles(responseData: CheckStatusResponse): void {
    const files = responseData.data.files || [];
    const preloaded: PreloadedFiles = {
      govId: [],
      prescription: [],
      proofOfTravel: []
    };

    files.forEach((file: any) => {
      const preloadedFile: PreloadedFile = {
        filename: file.filename,
        presignedUrl: file.presigned_url,
        s3_key: file.s3_key
      };

      if (file.type === 'GOVERNMENT_ID') {
        preloaded.govId.push(preloadedFile);
      } else if (file.type === 'PRESCRIPTION') {
        preloaded.prescription.push(preloadedFile);
      } else if (file.type === 'PROOF_OF_TRAVEL') {
        preloaded.proofOfTravel.push(preloadedFile);
      }
    });

    console.log(responseData)
    console.log(preloaded)

    this.setPreloadedFiles(preloaded);
    this.setRejectionReason(responseData.data.approval_details?.rejected_reason || '');
  }

  mapPatientToRegistrationData(responseData: GetPatientResponse): void {
    console.log(responseData)
    this.registrationData = {
      ...this.registrationData,
      firstName: responseData.data?.patient?.firstname || '',
      middleInitial: responseData.data?.patient?.middle_name || responseData.data?.patient?.middlename || '',
      lastName: responseData.data?.patient?.lastname || '',
      suffix: responseData.data?.patient?.suffix || '',
      mobileNumber: responseData.data?.patient?.mobile_number || '',
      email: responseData.data?.patient?.email || '',
      region: responseData.data?.patient?.region || null,
      city: responseData.data?.patient?.city || null,
      // patient_id removed - will be derived from authentication context
      // Preserve existing file arrays and metadata
      validId: this.registrationData?.validId || [],
      prescription: this.registrationData?.prescription || [],
      proofOfTravel: this.registrationData?.proofOfTravel || [],
      countryCode: this.registrationData?.countryCode || '',
      prescribedProduct: this.registrationData?.prescribedProduct || ''
    };
  }

  setMobileNumber(mobileNumber: string): void {
    this.registrationData!.mobileNumber = mobileNumber;
  }

  setPatientConsentAccepted(accepted: boolean): void {
    this.patientConsentAccepted = accepted;
  }

  getPatientConsentAccepted(): boolean {
    return this.patientConsentAccepted;
  }

  setTermsConditionsAccepted(accepted: boolean): void {
    this.termsConditionsAccepted = accepted;
  }

  getTermsConditionsAccepted(): boolean {
    return this.termsConditionsAccepted;
  }

  setPrescriptionFiles(files: File[]): void {
    this.uploadedPrescriptionFiles = files;
  }

  getPrescriptionFiles(): File[] {
    return this.uploadedPrescriptionFiles;
  }

  setProofOfTravelFiles(files: File[]): void {
    this.uploadedProofOfTravelFiles = files;
  }

  getProofOfTravelFiles(): File[] {
    return this.uploadedProofOfTravelFiles;
  }

  setPrescriptionFormState(state: { travelingAbroad: boolean; patientConsent: boolean; termsConditions: boolean }): void {
    this.prescriptionFormState = state;
  }

  getPrescriptionFormState(): { travelingAbroad: boolean; patientConsent: boolean; termsConditions: boolean } | null {
    return this.prescriptionFormState;
  }

  clearPrescriptionState(): void {
    this.uploadedPrescriptionFiles = [];
    this.uploadedProofOfTravelFiles = [];
    this.prescriptionFormState = null;
    this.modalEligibilityConfirmed = false;
  }
}
